import { Link } from "@tanstack/react-router";
import { AlertTriangle, Check, Lightbulb, LoaderCircle, Send } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/features/auth/AuthContext";
import { trackEvent } from "@/lib/analytics";

import {
  listEpisodeReflections,
  reactToEpisodeReflection,
  reportEpisodeReflection,
  saveEpisodeReflection,
} from "./api";
import type { EpisodeReflection } from "./types";

function formatCommunityDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Recientemente";
  return new Intl.DateTimeFormat("es-CO", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

function CommunityAvatar({
  displayName,
  photoUrl,
  fallbackPhotoUrl,
}: {
  displayName: string;
  photoUrl?: string | null;
  fallbackPhotoUrl?: string | null;
}) {
  const photoUrls = [photoUrl, fallbackPhotoUrl].filter(
    (value, index, values): value is string => Boolean(value) && values.indexOf(value) === index,
  );
  const [photoIndex, setPhotoIndex] = useState(0);

  useEffect(() => {
    setPhotoIndex(0);
  }, [fallbackPhotoUrl, photoUrl]);

  if (photoUrls[photoIndex]) {
    return (
      <img
        src={photoUrls[photoIndex]}
        alt=""
        referrerPolicy="no-referrer"
        className="h-9 w-9 shrink-0 rounded-full object-cover"
        onError={() => setPhotoIndex((current) => current + 1)}
      />
    );
  }

  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black text-sm font-semibold text-white">
      {displayName.charAt(0).toUpperCase()}
    </span>
  );
}

export function EpisodeCommunity({
  episodeSlug,
  episodeTitle,
  privateNotes,
}: {
  episodeSlug: string;
  episodeTitle: string;
  privateNotes: string;
}) {
  const { user } = useAuth();
  const [reflections, setReflections] = useState<EpisodeReflection[]>([]);
  const [myReflection, setMyReflection] = useState<EpisodeReflection>();
  const [shareAuthorized, setShareAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pendingAction, setPendingAction] = useState("");
  const [loadError, setLoadError] = useState("");
  const publicNoteLength = privateNotes.trim().length;
  const publicNoteIsValid = publicNoteLength >= 20 && publicNoteLength <= 800;

  const loadCommunity = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const data = await listEpisodeReflections(user, episodeSlug);
      setReflections(data.reflections);
      setMyReflection(data.viewer?.myReflection);
    } catch (error) {
      setLoadError(
        error instanceof Error ? error.message : "No pudimos cargar el círculo del episodio.",
      );
    } finally {
      setLoading(false);
    }
  }, [episodeSlug, user]);

  useEffect(() => {
    void loadCommunity();
  }, [loadCommunity]);

  function updatePublicAuthorization(authorized: boolean) {
    setShareAuthorized(authorized);
  }

  async function publishReflection() {
    if (!user) return;
    const content = privateNotes.trim();
    if (!publicNoteIsValid) {
      toast.error("«Tu conclusión» debe tener entre 20 y 800 caracteres.");
      return;
    }
    setSaving(true);
    try {
      const reflection = await saveEpisodeReflection(user, {
        episodeSlug,
        episodeTitle,
        content,
      });
      setMyReflection(reflection);
      setShareAuthorized(false);
      await loadCommunity();
      void trackEvent("episode_reflection_publish", {
        episode_slug: episodeSlug,
        updated: Boolean(myReflection),
      });
      toast.success(
        reflection.status === "pending"
          ? "Guardamos el cambio para revisión."
          : myReflection
            ? "Tu reflexión fue actualizada."
            : "Tu reflexión ya forma parte del círculo.",
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos publicar tu reflexión.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleReaction(reflection: EpisodeReflection) {
    if (!user) return;
    setPendingAction(reflection.id);
    try {
      const result = await reactToEpisodeReflection(user, reflection.id);
      setReflections((current) =>
        current.map((item) =>
          item.id === reflection.id
            ? { ...item, reacted: result.reacted, reactionCount: result.reactionCount }
            : item,
        ),
      );
      void trackEvent("episode_reflection_react", {
        episode_slug: episodeSlug,
        reacted: result.reacted,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos guardar tu reacción.");
    } finally {
      setPendingAction("");
    }
  }

  async function reportReflection(reflection: EpisodeReflection) {
    if (!user) return;
    setPendingAction(reflection.id);
    try {
      const result = await reportEpisodeReflection(user, reflection.id);
      toast.success(
        result.alreadyReported
          ? "Ya habías reportado esta reflexión."
          : "Recibimos el reporte y lo revisaremos.",
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos enviar el reporte.");
    } finally {
      setPendingAction("");
    }
  }

  return (
    <div className="mt-3 border-t border-black/10 pt-3">
      {user ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={shareAuthorized}
              onChange={(event) => updatePublicAuthorization(event.target.checked)}
              className="h-4 w-4 cursor-pointer accent-black"
            />
            Compartir públicamente
          </label>
          <Button
            type="button"
            size="sm"
            onClick={() => void publishReflection()}
            disabled={saving || !shareAuthorized || !publicNoteIsValid}
          >
            {saving ? <LoaderCircle className="animate-spin" /> : <Send />}
            {saving ? "Publicando…" : myReflection ? "Actualizar" : "Publicar"}
          </Button>
        </div>
      ) : (
        <Button asChild size="sm">
          <Link to="/acceso">Iniciar sesión para publicar</Link>
        </Button>
      )}

      {shareAuthorized && !publicNoteIsValid && (
        <p className="mt-2 text-xs text-black/50">Escribe entre 20 y 800 caracteres.</p>
      )}
      {myReflection && myReflection.status !== "published" && (
        <p className="mt-2 flex items-center gap-2 text-xs text-amber-700">
          <AlertTriangle className="h-4 w-4" />
          Pendiente de moderación.
        </p>
      )}

      {loading ? (
        <LoaderCircle className="mt-4 h-4 w-4 animate-spin text-black/35" aria-label="Cargando" />
      ) : loadError ? (
        <button
          type="button"
          className="mt-4 text-xs text-black/50 underline underline-offset-4"
          onClick={() => void loadCommunity()}
        >
          Reintentar cargar la comunidad
        </button>
      ) : reflections.length > 0 ? (
        <div className="mt-5 space-y-3 border-t border-black/10 pt-5">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-black/50">
            Comunidad · {reflections.length}
          </p>
          {reflections.map((reflection) => {
            const isMine = myReflection?.id === reflection.id;
            return (
              <article
                key={reflection.id}
                className="rounded-xl border border-black/10 bg-white p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <CommunityAvatar
                      displayName={reflection.displayName}
                      photoUrl={isMine ? user?.photoURL : reflection.photoUrl}
                      fallbackPhotoUrl={isMine ? reflection.photoUrl : undefined}
                    />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">
                        {reflection.displayName}
                        {isMine && <span className="ml-2 text-xs text-black/45">Tú</span>}
                      </p>
                      <p className="text-xs text-black/50">
                        {formatCommunityDate(reflection.updatedAt)}
                      </p>
                    </div>
                  </div>
                  {!isMine && user && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          type="button"
                          className="text-xs text-black/45 underline-offset-4 hover:text-black hover:underline"
                        >
                          Reportar
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-xl border-black bg-white text-black">
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Reportar esta reflexión?</AlertDialogTitle>
                          <AlertDialogDescription className="text-black/60">
                            Úsalo únicamente para contenido ofensivo, spam o información personal.
                            El equipo de Socrattica revisará el aporte.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => void reportReflection(reflection)}
                            disabled={pendingAction === reflection.id}
                          >
                            Enviar reporte
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
                <p className="mt-4 whitespace-pre-wrap text-sm leading-6 text-black/75">
                  {reflection.content}
                </p>
                <div className="mt-4 border-t border-black/10 pt-3">
                  {user ? (
                    <button
                      type="button"
                      disabled={pendingAction === reflection.id}
                      onClick={() => void toggleReaction(reflection)}
                      className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition disabled:cursor-wait disabled:opacity-50 ${
                        reflection.reacted
                          ? "border-black bg-black text-white"
                          : "border-black/20 bg-white text-black hover:border-black"
                      }`}
                    >
                      {reflection.reacted ? <Check className="h-3.5 w-3.5" /> : <Lightbulb />}
                      Me hizo pensar · {reflection.reactionCount}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-xs text-black/50">
                      <Lightbulb className="h-3.5 w-3.5" />
                      {reflection.reactionCount} persona
                      {reflection.reactionCount === 1 ? "" : "s"} dijeron “Me hizo pensar”
                    </span>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
